/**
 * Spacecraft trajectories: segments, frame resolution and `stateAt` (sprint plan §5.3).
 *
 * 1. Inside an encounter segment the position is `body(t) + q(t)`, anchored to astronomy-engine's body position, so
 *    the geometry relative to the body is exact no matter how far the planet ephemeris is off.
 * 2. Otherwise the base ("cruise") segment is used: Sun-centred for HORIZONS spacecraft, or centred on a body for a
 *    reconstructed Earth–Moon trajectory (S4-08: Apollo's base segment is geocentric, its encounter selenocentric).
 * 3. Encounter segments extend beyond the bubble (validated by build-data). Between the segment start and the bubble
 *    entry (and between exit and segment end) the position is cross-faded from cruise to encounter data, so the
 *    switch never jumps, and it happens outside any bubble.
 */
import type { TrajectoryData } from './data';
import { findInterval, hermiteAt } from './hermite';
import { type Vec3, add, copy, lerp, sub, vec3 } from './vec3';

/** Provides heliocentric body positions (km) at a time (days since J2000 TDB). */
export type BodyPositionFn = (body: string, tdb: number) => Vec3;

export interface Segment {
  center: string;
  kind: 'cruise' | 'encounter';
  t: Float64Array;
  s: Float64Array;
  start: number;
  end: number;
  /** Bubble entry/exit (encounter segments); the cross-fade zones lie outside. */
  innerStart: number;
  innerEnd: number;
  hint: number;
}

export interface SpacecraftState {
  /** Heliocentric position, km. */
  p: Vec3;
  /** Heliocentric velocity, km/day (cross-fade derivative neglected). */
  v: Vec3;
  /** 'sun' or the body whose encounter segment is active. */
  frame: string;
  /** Position relative to `frame` (equals `p` for 'sun'). */
  q: Vec3;
  /** Velocity relative to `frame`, km/day (equals `v` for 'sun'). */
  w: Vec3;
}

export function createState(): SpacecraftState {
  return { p: vec3(), v: vec3(), frame: 'sun', q: vec3(), w: vec3() };
}

/** Half step of the central difference for the body velocity, days (1 min). */
const BODY_DT = 1 / 1440;

const smoothstep = (x: number): number => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));

export class Trajectory {
  readonly id: string;
  readonly cruise: Segment;
  readonly encounters: Segment[];
  private readonly tmpP = vec3();
  private readonly tmpV = vec3();
  private readonly tmpQ = vec3();
  private readonly tmpW = vec3();
  private readonly bodyV = vec3();
  private readonly bodyP = vec3();
  private readonly baseV = vec3();

  constructor(data: TrajectoryData) {
    this.id = data.spacecraft;
    const segments = data.segments.map((d): Segment => {
      const t = d.t instanceof Float64Array ? d.t : Float64Array.from(d.t);
      const start = t[0]!;
      const end = t[t.length - 1]!;
      return {
        center: d.center,
        kind: d.kind === 'cruise' ? 'cruise' : 'encounter',
        t,
        s: d.s instanceof Float64Array ? d.s : Float64Array.from(d.s),
        start,
        end,
        innerStart: d.inner?.[0] ?? start,
        innerEnd: d.inner?.[1] ?? end,
        hint: 0,
      };
    });
    const cruise = segments.find((s) => s.kind === 'cruise');
    if (!cruise) throw new Error(`${data.spacecraft}: trajectory has no base (cruise) segment`);
    this.cruise = cruise;
    this.encounters = segments.filter((s) => s.kind === 'encounter');
    this.start = Math.min(...segments.map((s) => s.start));
    this.end = Math.max(...segments.map((s) => s.end));
  }

  /**
   * Coverage: the union of all segments. An encounter segment can reach a little past the cruise data when the
   * trajectory ends inside the bubble (Huygens: the 12 h cruise grid ends 10 h before the landing).
   */
  private readonly start: number;
  private readonly end: number;

  /** [start, end] in days since J2000 TDB. */
  coverage(): [number, number] {
    return [this.start, this.end];
  }

  /** The encounter segment covering `t`, if any. */
  encounterAt(t: number): Segment | undefined {
    for (const seg of this.encounters) if (t >= seg.start && t <= seg.end) return seg;
    return undefined;
  }

  private evalSegment(seg: Segment, t: number, pos: Vec3, vel: Vec3): void {
    seg.hint = findInterval(seg.t, t, seg.hint);
    hermiteAt(seg.t, seg.s, seg.hint, t, pos, vel);
  }

  /** Velocity of `body` at `t` (central difference, ±1 min), or zero without `withVelocity`. */
  private bodyVelocity(body: string, t: number, bodyPos: BodyPositionFn, withVelocity: boolean, out: Vec3): Vec3 {
    if (!withVelocity) {
      out.x = out.y = out.z = 0;
      return out;
    }
    // bodyPos may return a shared vector: each call is read before the next
    copy(out, bodyPos(body, t + BODY_DT));
    const before = bodyPos(body, t - BODY_DT);
    out.x = (out.x - before.x) / (2 * BODY_DT);
    out.y = (out.y - before.y) / (2 * BODY_DT);
    out.z = (out.z - before.z) / (2 * BODY_DT);
    return out;
  }

  /** The base segment at `t`: heliocentric `p`, `v` and, relative to its centre, `q`, `w`. */
  private evalBase(
    t: number,
    bodyPos: BodyPositionFn,
    withVelocity: boolean,
    p: Vec3,
    v: Vec3,
    q: Vec3,
    w: Vec3,
  ): void {
    const base = this.cruise;
    this.evalSegment(base, t, q, w);
    if (base.center === 'sun') {
      copy(p, q);
      copy(v, w);
      return;
    }
    const vb = this.bodyVelocity(base.center, t, bodyPos, withVelocity, this.baseV);
    add(p, bodyPos(base.center, t), q);
    add(v, vb, w);
  }

  /**
   * State at `t` (days since J2000 TDB), written into `out`. Returns false outside the coverage (spacecraft hidden).
   * With `withVelocity = false` the heliocentric velocity inside encounters is only the body-relative one (cheaper).
   */
  stateAt(t: number, bodyPos: BodyPositionFn, out: SpacecraftState, withVelocity = true): boolean {
    if (!(t >= this.start && t <= this.end)) return false;
    const enc = this.encounterAt(t);
    if (!enc) {
      if (!(t >= this.cruise.start && t <= this.cruise.end)) return false;
      this.evalBase(t, bodyPos, withVelocity, out.p, out.v, out.q, out.w);
      out.frame = this.cruise.center;
      return true;
    }
    // Encounter segments hold the body-relative state; the body's velocity (central difference, ±1 min) makes the
    // velocity heliocentric. (skipped with withVelocity = false: line sampling needs positions only, and each lookup
    // is an ephemeris call)
    const vb = this.bodyVelocity(enc.center, t, bodyPos, withVelocity, this.bodyV);
    const b = copy(this.bodyP, bodyPos(enc.center, t));
    this.evalSegment(enc, t, out.q, out.w);
    add(out.p, b, out.q);
    add(out.v, vb, out.w);
    out.frame = enc.center;
    let w = 1;
    if (t < enc.innerStart) w = smoothstep((t - enc.start) / (enc.innerStart - enc.start));
    else if (t > enc.innerEnd) w = smoothstep((enc.end - t) / (enc.end - enc.innerEnd));
    if (w < 1) {
      // Cross-fade from the base data (outside the bubble), in the heliocentric frame.
      this.evalBase(t, bodyPos, withVelocity, this.tmpP, this.tmpV, this.tmpQ, this.tmpW);
      lerp(out.p, this.tmpP, out.p, w);
      lerp(out.v, this.tmpV, out.v, w);
      sub(out.q, out.p, b);
      sub(out.w, out.v, vb);
    }
    return true;
  }

  /**
   * Chronological sample times for drawing the trajectory: cruise times outside encounter segments plus all encounter
   * times. The data is decimated (D4), so samples can be months or years apart where the path is smooth; every data
   * interval is subdivided until the direction of motion turns by at most LINE_MAX_TURN and the chord stays below
   * LINE_MAX_CHORD of the distance to the segment centre — a polyline through the Hermite curve that looks smooth.
   */
  sampleTimes(bands: (center: string) => readonly MoonBand[] = () => NO_BANDS): Float64Array {
    const times: number[] = [];
    const push = (seg: Segment, keep: (t: number) => boolean): void => {
      const { t, s } = seg;
      const segBands = bands(seg.center);
      for (let i = 0; i < t.length; i++) {
        if (keep(t[i]!)) times.push(t[i]!);
        if (i + 1 === t.length) break;
        const n = Math.min(LINE_MAX_SUBDIVISIONS * 4, Math.max(subdivisions(s, i), bandSubdivisions(s, i, segBands)));
        for (let k = 1; k < n; k++) {
          const tk = t[i]! + ((t[i + 1]! - t[i]!) * k) / n;
          if (keep(tk)) times.push(tk);
        }
      }
    };
    push(this.cruise, (t) => !this.encounterAt(t));
    for (const seg of this.encounters) push(seg, () => true);
    times.sort((a, b) => a - b);
    return Float64Array.from(times);
  }
}

/**
 * An orbit around the segment's centre where a bubble magnifies the trail: a moon's around its planet (nested
 * bubbles, S3-07) or a planet's around the Sun (any trail that a planet's *current* bubble passes over later is
 * magnified, Sprint 2 rule). Within `band` km of the orbit radius, line segments are at most `maxChord` km long, so
 * the trail stays a smooth curve on screen.
 */
export interface MoonBand {
  orbit: number;
  band: number;
  maxChord: number;
}

const NO_BANDS: readonly MoonBand[] = [];

/** Extra subdivisions of the data interval [i, i + 1] where it touches a moon band. */
function bandSubdivisions(s: Float64Array, i: number, bands: readonly MoonBand[]): number {
  if (bands.length === 0) return 1;
  const a = 6 * i;
  const b = a + 6;
  const ra = Math.hypot(s[a]!, s[a + 1]!, s[a + 2]!);
  const rb = Math.hypot(s[b]!, s[b + 1]!, s[b + 2]!);
  const lo = Math.min(ra, rb);
  const hi = Math.max(ra, rb);
  const chord = Math.hypot(s[b]! - s[a]!, s[b + 1]! - s[a + 1]!, s[b + 2]! - s[a + 2]!);
  let n = 1;
  for (const band of bands) {
    // the interval's radial range (widened by the chord: the path may dip inwards between the samples) meets the band
    if (lo - chord > band.orbit + band.band || hi < band.orbit - band.band) continue;
    n = Math.max(n, Math.ceil(chord / band.maxChord));
  }
  return n;
}

/** Line sampling (sampleTimes): maximum turn of the direction of motion per line segment, and chord / distance. */
const LINE_MAX_TURN = (4 * Math.PI) / 180;
const LINE_MAX_CHORD = 0.05;
const LINE_MAX_SUBDIVISIONS = 64;

/** Number of line segments for the data interval [i, i + 1] of a flat state array. */
function subdivisions(s: Float64Array, i: number): number {
  const a = 6 * i;
  const b = a + 6;
  const dot = s[a + 3]! * s[b + 3]! + s[a + 4]! * s[b + 4]! + s[a + 5]! * s[b + 5]!;
  const va = Math.hypot(s[a + 3]!, s[a + 4]!, s[a + 5]!);
  const vb = Math.hypot(s[b + 3]!, s[b + 4]!, s[b + 5]!);
  const turn = va > 0 && vb > 0 ? Math.acos(Math.min(1, Math.max(-1, dot / (va * vb)))) : 0;
  const chord = Math.hypot(s[b]! - s[a]!, s[b + 1]! - s[a + 1]!, s[b + 2]! - s[a + 2]!);
  const r = Math.min(Math.hypot(s[a]!, s[a + 1]!, s[a + 2]!), Math.hypot(s[b]!, s[b + 1]!, s[b + 2]!));
  const n = Math.max(1, Math.ceil(turn / LINE_MAX_TURN), r > 0 ? Math.ceil(chord / (LINE_MAX_CHORD * r)) : 1);
  return Math.min(n, LINE_MAX_SUBDIVISIONS);
}
